import { collapseErrors, extractErrors } from "./api";
import { notify } from "./notify";

/** Replace with an actual error logged in the future if needed, e.g. Sentry */
export const logError = (err: string) => {
  console.error(err);
};

/** A generic function to inform the user that there has been an error. */
export const displayError = (err: any) => {
  notify(toErrorText(err), {
    position: "bottom-left",
    type: "error",
  });
};

const toErrorText = (value: any): string => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "An error occurred.";
  if (Array.isArray(value)) {
    return value.map(toErrorText).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "An error occurred.";
    }
  }

  return String(value);
};

/** A generic function to inform the user that an action was successful. */
export const displaySuccess = (message: string) => {
  notify(message, {
    position: "bottom-left",
    type: "success",
  });
};

/** Wrapper function for the above functions. To be used with react-query. */
export const handleQueryError =
  (defaultError: string, includeErrorKeys = true) =>
  (err: any) => {
    displayError(
      collapseErrors(extractErrors(err), includeErrorKeys) || defaultError
    );
    logError(err);
  };

export const extractErrorMessage = (
  error: any,
  defaultError: string
): string => {
  const errorResponseData = error.response?.data;
  if (!errorResponseData) return defaultError;

  const exception = errorResponseData?.exception;

  const handleArrayException = (): string | undefined => {
    return typeof exception[0] === "string" ? exception[0] : undefined;
  };

  // The first message found at any depth, so every shape the API sends reads
  // as a sentence: `{name: ["…"]}`, `{name: [{code: ["…"]}]}`, and a bulk
  // endpoint's per-row `{0: {name: ["…"]}}` (e.g. common/team/bulk/).
  const firstMessage = (value: any): string | undefined => {
    if (typeof value === "string" || value instanceof String) {
      return String(value);
    }
    if (value && typeof value === "object") {
      for (const item of Object.values(value)) {
        const message = firstMessage(item);
        if (message) return message;
      }
    }
    return undefined;
  };

  const handleObjectException = () => firstMessage(exception);

  return exception && typeof exception === "string"
    ? exception
    : Array.isArray(exception)
    ? handleArrayException() || defaultError
    : typeof exception === "object"
    ? handleObjectException() || defaultError
    : defaultError;
};

export const showError = (error: any, defaultError: string) => {
  const errorMessage = extractErrorMessage(error, defaultError);
  displayError(toErrorText(errorMessage));
  logError(error);
};

/**
 * Maps a DRF-style field-error response (`{ field: ["message"] }`) onto Formik's
 * `setErrors` for the given field names, so the message renders under the right
 * input instead of only as a toast. Falls back to `showError` when the response
 * doesn't contain any of the given fields (e.g. a plain `{"detail": "..."}`).
 * Returns true if field-level errors were found and applied.
 */
export const applyServerFieldErrors = (
  error: any,
  fieldNames: string[],
  setErrors: (errors: Record<string, string>) => void,
  defaultError: string
): boolean => {
  const data = error?.response?.data;
  const fieldErrors: Record<string, string> = {};

  if (data && typeof data === "object") {
    fieldNames.forEach((field) => {
      const value = data[field];
      if (Array.isArray(value) && value.length > 0) {
        fieldErrors[field] = toErrorText(value[0]);
      } else if (typeof value === "string") {
        fieldErrors[field] = value;
      }
    });
  }

  if (Object.keys(fieldErrors).length > 0) {
    setErrors(fieldErrors);
    return true;
  }

  showError(error, defaultError);
  return false;
};

export const showSuccess = (message: string) => {
  displaySuccess(message);
};
